// frontend/src/contexts/AuthContext.jsx - VERSIÓN ROBUSTA FINAL
import React, { createContext, useState, useCallback, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import authService from '../services/authService';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(() => {
    // Solo loading inicial si hay token
    return !!localStorage.getItem('token');
  });
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Verificar autenticación sin causar loops
  const checkAuth = useCallback(async () => {
    
    
    const token = localStorage.getItem('token');
    if (!token) {
      
      setUser(null);
      setLoading(false);
      setInitialized(true);
      return;
    }

    // Verificar expiración del token
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      if (decoded.exp < currentTime) {
      
        localStorage.removeItem('token');
        setUser(null);
        setLoading(false);
        setInitialized(true);
        return;
      }
    } catch (tokenError) {
      
      localStorage.removeItem('token');
      setUser(null);
      setLoading(false);
      setInitialized(true);
      return;
    }

    // Verificar con el servidor
    try {
      setLoading(true);
      
      const response = await authService.getCurrentUser();
      
     
      setUser(response.data);
      setError(null);
    } catch (authError) {
      console.error('❌ [AuthContext] Auth verification failed:', authError.response?.status);
      
      // Solo limpiar si es un error real de autenticación (401/403)
      if (authError.response?.status === 401 || authError.response?.status === 403) {
         
        localStorage.removeItem('token');
        setUser(null);
      } else {
    
      }
      
      setError(authError.response?.data?.message || 'Error de verificación');
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  const login = async (credentials) => {
    setLoading(true);
    setError(null);
    
    try {
       
      const response = await authService.login(credentials);
      
      localStorage.setItem('token', response.data.token);
      setUser(response.data);
      setInitialized(true);
      
       
      return response.data;
    } catch (error) {
      console.error('❌ [AuthContext] Login failed:', error);
      setError(error.response?.data?.message || 'Error al iniciar sesión');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
     
    setLoading(true);
    
    try {
      await authService.logout();
    } catch (error) {
      console.error('⚠️ [AuthContext] Logout API error (non-critical):', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      setLoading(false);
      setInitialized(true);
       
    }
  };

  const forgotPassword = async (email) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.forgotPassword(email);
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Error al solicitar recuperación');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (token, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.resetPassword(token, password);
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Error al restablecer contraseña');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  // Verificación inicial
  useEffect(() => {
    if (!initialized) {
      checkAuth();
    }
  }, [checkAuth, initialized]);

   
  const contextValue = {
    user,
    loading,
    error,
    login,
    logout,
    forgotPassword,
    resetPassword,
    checkAuth,
    updateUser,
    isAuthenticated: !!user,
    initialized,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};